import { cn } from '@/lib/cn';
import { Check, Copy, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface CopyButtonProps {
  text: string;
  label?: string;
  /** 기본 Copy 대신 표시할 lucide 아이콘. 두 개 이상 나란히 둘 때 시각 구분용. */
  icon?: LucideIcon;
  className?: string;
  size?: 'xs' | 'sm';
}

export function CopyButton({
  text,
  label = '복사',
  icon: Icon = Copy,
  className,
  size = 'xs',
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const iconSize = size === 'sm' ? 'size-3.5' : 'size-3';
  const boxSize = size === 'sm' ? 'size-6' : 'size-5';

  async function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — 일부 비-HTTPS 환경에서는 clipboard API 실패 가능.
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={copied ? '복사됨' : label}
      title={copied ? '복사됨' : label}
      className={cn(
        'inline-flex items-center justify-center rounded-md',
        boxSize,
        'text-foreground-muted hover:text-foreground hover:bg-surface-muted/60',
        'focus-visible:outline-none focus-visible:text-foreground focus-visible:bg-surface-muted/60',
        'transition-colors',
        className,
      )}
    >
      {copied ? <Check className={cn(iconSize, 'text-accent')} /> : <Icon className={iconSize} />}
    </button>
  );
}
